import { Capacitor } from "@capacitor/core"
import moment from "moment-timezone"
import { Health } from "@/plugins/Health"
import { fetchWithAuth } from "@/utils/api-client"
import EventBus from "@/utils/eventBus"
import { getName } from "@/utils/mealLib"

const SOURCE = "android.healthconnect"
const HEALTH_CONNECT_MEAL_TYPE = {
    UNKNOWN: 0,
    BREAKFAST: 1,
    LUNCH: 2,
    DINNER: 3,
    SNACK: 4,
} as const
const HEALTH_CONNECT_MEAL_TYPE_BY_NAME: Record<string, number> = {
    breakfast: HEALTH_CONNECT_MEAL_TYPE.BREAKFAST,
    lunch: HEALTH_CONNECT_MEAL_TYPE.LUNCH,
    dinner: HEALTH_CONNECT_MEAL_TYPE.DINNER,
    snack: HEALTH_CONNECT_MEAL_TYPE.SNACK,
}

let cachedNutritionWrite: boolean | null = null
let lastPermissionCheck = 0

async function canWriteNutrition(): Promise<boolean> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") {
        return false
    }
    const now = Date.now()
    if (cachedNutritionWrite !== null && now - lastPermissionCheck < 30_000) {
        return cachedNutritionWrite
    }
    try {
        const { available } = await Health.isAvailable()
        if (!available) {
            cachedNutritionWrite = false
            lastPermissionCheck = now
            return false
        }
        const perms = await Health.getGrantedPermissions().catch(() => ({ granted: [] }))
        const granted = Array.isArray(perms?.granted)
            ? perms.granted.some((s) => String(s).includes("WRITE_NUTRITION"))
            : false
        cachedNutritionWrite = granted
        lastPermissionCheck = now
        return granted
    } catch (err) {
        console.warn("Health Connect availability check failed", err)
        cachedNutritionWrite = false
        lastPermissionCheck = now
        return false
    }
}

function normalizeMass(value: unknown, fallbackUnit: string): string | undefined {
    if (value === null || value === undefined) return undefined
    if (typeof value === "number" && !Number.isNaN(value)) {
        return `${value}${fallbackUnit}`
    }
    const str = String(value).trim()
    if (!str) return undefined
    if (/[a-zA-Z]/.test(str)) return str
    return `${str}${fallbackUnit}`
}

function parseCalories(value: unknown): number | undefined {
    if (value === null || value === undefined) return undefined
    if (typeof value === "number") return Number.isNaN(value) ? undefined : value
    const numeric = Number(String(value).replace(/[^0-9.\-]/g, ""))
    return Number.isNaN(numeric) ? undefined : numeric
}

function resolveMealTypeCode(meal: any): number {
    const raw = (meal?.meal_type ?? meal?.mealType ?? meal?.entry?.meal_type) as unknown
    if (typeof raw === "string") {
        const key = raw.trim().toLowerCase()
        if (key && HEALTH_CONNECT_MEAL_TYPE_BY_NAME[key] !== undefined) {
            return HEALTH_CONNECT_MEAL_TYPE_BY_NAME[key]
        }
    }
    return HEALTH_CONNECT_MEAL_TYPE.UNKNOWN
}

function resolveMealName(meal: any): string | undefined {
    const candidate = getName(meal)
    if (typeof candidate !== "string") return undefined
    const trimmed = candidate.trim()
    return trimmed ? trimmed : undefined
}

function buildNutritionWriteOptions(meal: any) {
    if (!meal || typeof meal !== "object") return null
    const info = meal.entry?.nutritional_info
    if (!info) return null

    const ts = typeof meal.timestamp === "number" ? meal.timestamp : null
    const start = ts ? moment.unix(ts).toISOString() : moment().toISOString()
    const end = moment(start).add(5, "minutes").toISOString()

    const mealType = resolveMealTypeCode(meal)
    const mealName = resolveMealName(meal)

    return {
        startTime: start,
        endTime: end,
        calories: parseCalories(info.calories),
        trans_fat: normalizeMass(info.trans_fat, "g"),
        fat_saturated: normalizeMass(info.fat_saturated, "g"),
        fat_unsaturated: normalizeMass(info.fat_unsaturated, "g"),
        cholesterol: normalizeMass(info.cholesterol, "mg"),
        carbs: normalizeMass(info.carbs, "g"),
        fiber: normalizeMass(info.fiber, "g"),
        sugars: normalizeMass(info.sugars, "g"),
        protein: normalizeMass(info.protein, "g"),
        sodium: normalizeMass(info.sodium, "mg"),
        mealType,
        mealName,
    }
}

async function getNutritionMapping(recordId: number) {
    try {
        const res = await fetchWithAuth(`/api/health-connect/nutrition/map/${recordId}`, { method: "GET" })
        if (!res.ok) return null
        const data = await res.json()
        if (!data?.identifier) return null
        return data
    } catch (err) {
        console.warn("Failed to fetch nutrition mapping", err)
        return null
    }
}

async function upsertNutritionMapping(recordId: number, identifier: string, time?: string) {
    try {
        await fetchWithAuth("/api/health-connect/nutrition/upsert", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                identifier,
                recordId,
                source: SOURCE,
                time,
            }),
        })
    } catch (err) {
        console.warn("Failed to persist nutrition mapping", err)
    }
}

async function deleteNutritionMapping(identifier?: string) {
    if (!identifier) return
    try {
        await fetchWithAuth("/api/health-connect/nutrition/delete", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ identifier, source: SOURCE }),
        })
    } catch (err) {
        console.warn("Failed to delete nutrition mapping", err)
    }
}

export async function upsertMealNutrition(meal: any) {
    if (!meal?.id) return
    if (!(await canWriteNutrition())) return

    const options = buildNutritionWriteOptions(meal)
    if (!options) return

    try {
        const mapping = await getNutritionMapping(meal.id)
        if (mapping?.identifier) {
            try {
                await Health.deleteNutrition({
                    id: mapping.identifier,
                    time: options.startTime,
                })
            } catch (err) {
                console.warn("Failed to delete previous nutrition record", err)
            }
        }

        const result = await Health.writeNutrition(options)
        if (result?.id) {
            await upsertNutritionMapping(meal.id, result.id, options.startTime)
        }
    } catch (err) {
        console.warn("Failed to write nutrition to Health Connect", err)
    }
}

export async function deleteMealNutrition(meal: any) {
    if (!meal?.id) return
    if (!(await canWriteNutrition())) return

    const options = buildNutritionWriteOptions(meal)
    const mapping = await getNutritionMapping(meal.id)

    try {
        await Health.deleteNutrition({
            id: mapping?.identifier,
            time: options?.startTime,
        })
    } catch (err) {
        console.warn("Failed to delete nutrition from Health Connect", err)
    }

    await deleteNutritionMapping(mapping?.identifier)
}

export function enableMealNutritionSync() {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") {
        return () => {}
    }
    const offAdded = EventBus.on("meal-added", (meal) => { void upsertMealNutrition(meal) })
    const offUpdated = EventBus.on("meal-updated", (meal) => { void upsertMealNutrition(meal) })
    const offDeleted = EventBus.on("meal-deleted", (meal) => { void deleteMealNutrition(meal) })
    let disposed = false
    return () => {
        if (disposed) return
        disposed = true
        offAdded?.()
        offUpdated?.()
        offDeleted?.()
    }
}

